import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Download, Search, RefreshCw, Info } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const PER_PAGE_OPTIONS = [25, 50, 100];

function fmtDate(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return d; }
}

function Pagination({ page, totalPages, total, pageSize, onPage }) {
  if (totalPages <= 1) return null;
  const start = Math.max(1, page - 2);
  const end   = Math.min(totalPages, page + 2);
  const nums  = [];
  for (let i = start; i <= end; i++) nums.push(i);
  return (
    <div className="pagination" data-testid="cld-pagination">
      <span className="pagination-info">
        {((page - 1) * pageSize + 1).toLocaleString()}–{Math.min(page * pageSize, total).toLocaleString()} of {total.toLocaleString()}
      </span>
      <button className="page-btn" disabled={page <= 1} onClick={() => onPage(1)}>«</button>
      <button className="page-btn" disabled={page <= 1} onClick={() => onPage(page - 1)}>‹</button>
      {nums.map(n => (
        <button key={n} className={`page-btn${n === page ? ' active' : ''}`} onClick={() => onPage(n)}>{n}</button>
      ))}
      <button className="page-btn" disabled={page >= totalPages} onClick={() => onPage(page + 1)}>›</button>
      <button className="page-btn" disabled={page >= totalPages} onClick={() => onPage(totalPages)}>»</button>
    </div>
  );
}

export default function CourseLearnerDetail() {
  const { class_id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // "enrolled" → no status filter (show all); "completed" → pre-select Completed
  const viewMode      = searchParams.get('view') || 'enrolled';
  const courseNameQS  = searchParams.get('course') || class_id;

  const [courseName]                      = useState(courseNameQS);
  const [search, setSearch]               = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [statusFilter, setStatusFilter]   = useState(viewMode === 'completed' ? 'Completed' : '');
  const [data, setData]                   = useState([]);
  const [total, setTotal]                 = useState(0);
  const [totalPages, setTotalPages]       = useState(1);
  const [page, setPage]                   = useState(1);
  const [perPage, setPerPage]             = useState(25);
  const [loading, setLoading]             = useState(false);
  const [exporting, setExporting]         = useState(false);

  const buildParams = (p, pp, q, st) => {
    const params = { page: p, page_size: pp };
    if (q)  params.search = q;
    if (st) params.status = st;
    return params;
  };

  const fetchData = useCallback(async (p, pp, q, st) => {
    setLoading(true);
    try {
      const r = await axios.get(
        `${API}/api/reports/course-activity/course-learners/${encodeURIComponent(class_id)}`,
        { params: buildParams(p, pp, q, st), withCredentials: true }
      );
      setData(r.data.data || []);
      setTotal(r.data.total || 0);
      setTotalPages(r.data.total_pages || 1);
      setPage(p);
    } catch (e) {
      console.error(e);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [class_id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const initialStatus = viewMode === 'completed' ? 'Completed' : '';
    fetchData(1, perPage, '', initialStatus);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleApply = useCallback(() => {
    setAppliedSearch(search);
    fetchData(1, perPage, search, statusFilter);
  }, [search, perPage, statusFilter, fetchData]);

  const handlePerPageChange = (val) => {
    const pp = Number(val);
    setPerPage(pp);
    fetchData(1, pp, appliedSearch, statusFilter);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams({ limit: 10000, offset: 0 });
      if (appliedSearch) params.set('search', appliedSearch);
      if (statusFilter)  params.set('status', statusFilter);
      const url = `${API}/api/reports/course-activity/course-learners/${encodeURIComponent(class_id)}/export?${params}`;
      const r = await axios.get(url, { withCredentials: true, responseType: 'blob' });
      const blob = new Blob([r.data], { type: 'text/csv' });
      const link = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = link; a.download = `course_${class_id}_learners.csv`; a.click();
      URL.revokeObjectURL(link);
    } catch (e) { console.error(e); }
    finally { setExporting(false); }
  };

  const pageTitle = 'Enrolled Learners';

  return (
    <div data-testid="course-learner-detail-page">
      {/* Breadcrumb / back */}
      <div style={{ fontSize: 12, color: '#666', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
        <button
          data-testid="cld-back-btn"
          onClick={() => navigate('/reports/course-activity')}
          style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, color: '#194449', fontWeight: 600, fontSize: 12, padding: 0 }}
        >
          <ArrowLeft size={13} /> Course Activity Report
        </button>
        <span style={{ color: '#9CA3AF' }}>/</span>
        <span>{courseName}</span>
      </div>

      <div className="page-header">
        <h1 style={{ fontSize: 20, wordBreak: 'break-word' }}>{courseName} — {pageTitle}</h1>
      </div>

      {/* Stat cards */}
      <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(3,1fr)', marginBottom: 16 }}>
        <div className="stat-card" data-testid="cld-stat-total">
          <div className="stat-value green">{total.toLocaleString()}</div>
          <div className="stat-label">{statusFilter ? `${statusFilter} Learners` : 'Enrolled Learners'}</div>
        </div>
        <div className="stat-card" style={{ background: '#f8fafc' }}>
          <div className="stat-label" style={{ marginBottom: 4 }}>Class ID</div>
          <div style={{ fontFamily: 'monospace', fontSize: 13, color: '#475569', wordBreak: 'break-all' }}>{class_id}</div>
        </div>
        <div className="stat-card" style={{ background: '#f8fafc' }}>
          <div className="stat-label" style={{ marginBottom: 4 }}>Status Filter</div>
          <div style={{ fontSize: 13, color: '#374151', fontWeight: 600 }}>{statusFilter || 'All Statuses'}</div>
        </div>
      </div>

      {/* Info note */}
      {statusFilter && (
        <div data-testid="cld-enrolled-note" style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '10px 14px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, marginBottom: 12, fontSize: 12, color: '#1e40af' }}>
          <Info size={14} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>Showing <strong>{total.toLocaleString()}</strong> {statusFilter.toLowerCase()} learners. Clear the status filter to see all enrolled learners.</span>
        </div>
      )}

      {/* Filters */}
      <div className="filter-bar">
        <div className="filter-group">
          <label className="filter-label">Search Learner</label>
          <div style={{ position: 'relative' }}>
            <Search size={13} style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF' }} />
            <input
              type="text"
              data-testid="cld-search-input"
              className="filter-input"
              placeholder="Name or email…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleApply()}
              style={{ paddingLeft: 28 }}
            />
          </div>
        </div>
        <div className="filter-group">
          <label className="filter-label">Status</label>
          <select
            className="filter-select"
            data-testid="cld-status-filter"
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
          >
            <option value="">All Statuses</option>
            <option value="Completed">Completed</option>
            <option value="In Progress">In Progress</option>
            <option value="Not Started">Not Started</option>
          </select>
        </div>
        <div className="filter-group">
          <label className="filter-label">Per page</label>
          <select className="filter-select" data-testid="cld-per-page" value={perPage} onChange={e => handlePerPageChange(e.target.value)}>
            {PER_PAGE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          <button className="btn btn-primary" onClick={handleApply} data-testid="cld-apply-btn">Apply</button>
          <button className="btn btn-secondary" onClick={() => { setSearch(''); setAppliedSearch(''); setStatusFilter(''); fetchData(1, perPage, '', ''); }} data-testid="cld-reset-btn">Reset</button>
        </div>
      </div>

      {/* Table */}
      <div className="table-container">
        <div className="table-header">
          <div>
            <span className="table-title">Enrolled Learners</span>
            <span className="table-count" style={{ marginLeft: 8 }}>({total.toLocaleString()} learners)</span>
          </div>
          <button
            data-testid="cld-export-btn"
            onClick={handleExport}
            disabled={exporting}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#217346', display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}
            title="Export learner list to CSV"
          >
            <Download size={16} /> {exporting ? 'Exporting…' : 'CSV'}
          </button>
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: 'center' }}>
            <RefreshCw size={20} style={{ animation: 'spin 1s linear infinite', color: '#9CA3AF' }} />
          </div>
        ) : data.length === 0 ? (
          <div className="empty-state">
            <h3>No learners found</h3>
            <p>Try adjusting your search or status filter</p>
          </div>
        ) : (
          <>
            <div className="table-scroll">
              <table data-testid="cld-table">
                <thead>
                  <tr>
                    <th>Learner Name</th>
                    <th>Email</th>
                    <th>Partner</th>
                    <th>Country</th>
                    <th>Status</th>
                    <th>Enrolled At</th>
                    <th>Started At</th>
                    <th>Completed At</th>
                    <th className="td-right">Score (%)</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((row, i) => (
                    <tr key={row.student_user_id || row.lms_id || i} data-testid={`cld-row-${i}`}>
                      <td style={{ maxWidth: 200 }} title={row.student_name || row.name}>{row.student_name || row.name || '—'}</td>
                      <td style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.email}>{row.email || '—'}</td>
                      <td>{row.partner_name || row.company || '—'}</td>
                      <td>{row.country || '—'}</td>
                      <td>
                        {row.status === 'Completed' || row.status === 'completed'
                          ? <span className="badge badge-green">Completed</span>
                          : row.status === 'In Progress' || row.status === 'in_progress'
                          ? <span className="badge badge-blue">In Progress</span>
                          : row.status
                          ? <span className="badge">{row.status}</span>
                          : <span style={{ color: '#9ca3af' }}>—</span>}
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(row.enrolled_at)}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(row.started_at)}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(row.completed_at)}</td>
                      <td className="td-right">{row.score !== null && row.score !== undefined && row.score !== '' ? `${row.score}%` : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination page={page} totalPages={totalPages} total={total} pageSize={perPage}
              onPage={p => fetchData(p, perPage, appliedSearch, statusFilter)} />
          </>
        )}
      </div>
    </div>
  );
}
